import PropTypes from 'prop-types';
import React from 'react';

import { Errors } from 'components/ui/errors';

import { GENERIC_ERROR_MESSAGE } from 'helpers/api_errors';
import { fetchTranslation } from '../../../../helpers/i18n';

// What the visitor is told when a request fails outright. Falls back to the app-wide English message, so a
// locale without the key still says something instead of rendering a missing-translation placeholder.
export function requestErrorMessage() {
  return fetchTranslation('request_error', null, GENERIC_ERROR_MESSAGE);
}

// The profile inputs GateAccountFields and AccountForm always render. The email, password and role inputs come and
// go with the step, so each form adds their keys itself.
export const PROFILE_FIELD_ERROR_KEYS = [
  'first_name_in_errors',
  'last_name_in_errors',
  'phone_number_in_errors',
  'company_in_errors',
  'job_title_in_errors'
];

// The labels the given inputs match their messages against. Read at render time, because window.translations is
// hydrated after this module loads.
export function errorStringsFor(keys) {
  return keys.map(key => fetchTranslation(key));
}

export function getErrorsForInput(errors, errorString) {
  const filteredErrors = Array.isArray(errorString)
    ? errors.filter(error => errorString.some(str => error.includes(str)))
    : errors.filter(error => error.includes(errorString));

  return filteredErrors;
}

function getOtherErrors(errors, errorStrings) {
  return errors.filter(error => !errorStrings.some(errorString => error.includes(errorString)));
}

// What a step shows when its request failed outright, rather than came back with reasons.
export function RequestError({ className }) {
  return <Errors className={className} error={requestErrorMessage()} role="alert" title={false}/>;
}

RequestError.propTypes = {
  className: PropTypes.string
};

// The messages no input claims: a refusal that names no field, or a request that failed outright. ErrorsForInput
// only shows a message under the input whose label it contains, so without this they are stored and shown nowhere.
export function OtherErrors({ errors, errorStrings = [] }) {
  return <Errors errors={getOtherErrors(errors, errorStrings)} role="alert" title={false}/>;
}

OtherErrors.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.string).isRequired,
  errorStrings: PropTypes.arrayOf(PropTypes.string)
};

export default function ErrorsForInput({ errors, errorString }) {
  const errorsForInput = getErrorsForInput(errors, errorString);
  if (errorsForInput.length === 0) return null;

  return (
    <div className="mt-2 d-flex flex-column gap-1">
      {errorsForInput.map(error => (
        <small className="text-danger" key={error}>{error}</small>
      ))}
    </div>
  );
}

ErrorsForInput.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
  errorString: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]).isRequired
};
